import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Inject token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);


// ── Auth ──────────────────────────────────────────────────
export const authService = {
  login: (email, password, tenant_id) =>
    api.post('/auth/login', { email, password, tenant_id }),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
};

// ── Tenants ───────────────────────────────────────────────
export const tenantService = {
  register: (data) => api.post('/tenants/register', data),
  getMe: () => api.get('/tenants/me'),
  update: (data) => api.put('/tenants/me', data),
};

// ── Users ─────────────────────────────────────────────────
export const userService = {
  list: (params) => api.get('/users', { params }),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// ── Clients ───────────────────────────────────────────────
export const clientService = {
  list: (params) => api.get('/clients', { params }),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// ── Assets ────────────────────────────────────────────────
export const assetService = {
  list: (params) => api.get('/assets', { params }),
  get: (id) => api.get(`/assets/${id}`),
  summary: () => api.get('/assets/summary'),
  create: (data) => api.post('/assets', data),
  update: (id, data) => api.put(`/assets/${id}`, data),
  delete: (id) => api.delete(`/assets/${id}`),
};

// ── Drivers ───────────────────────────────────────────────
export const driverService = {
  list: (params) => api.get('/drivers', { params }),
  get: (id) => api.get(`/drivers/${id}`),
  create: (data) => api.post('/drivers', data),
  update: (id, data) => api.put(`/drivers/${id}`, data),
};

// ── Vehicles ──────────────────────────────────────────────
export const vehicleService = {
  list: (params) => api.get('/vehicles', { params }),
  get: (id) => api.get(`/vehicles/${id}`),
  create: (data) => api.post('/vehicles', data),
  update: (id, data) => api.put(`/vehicles/${id}`, data),
};

// ── Tenders ───────────────────────────────────────────────
export const tenderService = {
  list: (params) => api.get('/tenders', { params }),
  get: (id) => api.get(`/tenders/${id}`),
  create: (data) => api.post('/tenders', data),
  update: (id, data) => api.put(`/tenders/${id}`, data),
};

// ── Contracts ─────────────────────────────────────────────
export const contractService = {
  list: (params) => api.get('/contracts', { params }),
  get: (id) => api.get(`/contracts/${id}`),
  create: (data) => api.post('/contracts', data),
  update: (id, data) => api.put(`/contracts/${id}`, data),
};

// ── Logistics ─────────────────────────────────────────────
export const logisticsService = {
  list: (params) => api.get('/logistics', { params }),
  today: () => api.get('/logistics/today'),
  get: (id) => api.get(`/logistics/${id}`),
  create: (data) => api.post('/logistics', data),
  update: (id, data) => api.put(`/logistics/${id}`, data),
  checkin: (id, data) => api.post(`/logistics/${id}/checkin`, data),
  checkout: (id, data) => api.post(`/logistics/${id}/checkout`, data),
};

// ── Maintenance ───────────────────────────────────────────
export const maintenanceService = {
  list: (params) => api.get('/maintenances', { params }),
  create: (data) => api.post('/maintenances', data),
  update: (id, data) => api.put(`/maintenances/${id}`, data),
};

// ── Financial ─────────────────────────────────────────────
export const financialService = {
  list: (params) => api.get('/financial', { params }),
  cashflow: (params) => api.get('/financial/cashflow', { params }),
  create: (data) => api.post('/financial', data),
  update: (id, data) => api.put(`/financial/${id}`, data),
  pay: (id, params) => api.put(`/financial/${id}/pay`, null, { params }),
};

// ── Invoices ──────────────────────────────────────────────
export const invoiceService = {
  list: (params) => api.get('/invoices', { params }),
  create: (data) => api.post('/invoices', data),
  emit: (id) => api.post(`/invoices/${id}/emit`),
  cancel: (id, reason) => api.post(`/invoices/${id}/cancel`, null, { params: { reason } }),
};

// ── Documents ─────────────────────────────────────────────
export const documentService = {
  list: (params) => api.get('/documents', { params }),
  upload: (file, meta) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/documents/upload', fd, {
      params: meta,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (id) => api.delete(`/documents/${id}`),
};

// ── Dashboard ─────────────────────────────────────────────
export const dashboardService = {
  get: () => api.get('/dashboard'),
  reportsAssets: () => api.get('/dashboard/reports/assets'),
  reportsRevenue: (year) => api.get('/dashboard/reports/revenue', { params: { year } }),
};

export const heatmapService = {
  getOperations: (params) => api.get('/logistics', { params: { limit: 500, ...params } }),
};

export const auditService = {
  list: (params) => api.get('/audit', { params }),
};

export default api;

// ── Reports ───────────────────────────────────────────────────────
const downloadFile = async (url, filename) => {
  const token = localStorage.getItem('token');
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error('Erro ao gerar arquivo');
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
};

export const reportsService = {
  assetQRCode: (id, code) => downloadFile(`/api/reports/assets/${id}/qrcode`, `qr_${code}.png`),
  allQRCodesPDF: () => downloadFile('/api/reports/assets/qrcodes/pdf', 'qrcodes_ativos.pdf'),
  assetsPDF: () => downloadFile('/api/reports/assets/pdf', 'relatorio_ativos.pdf'),
  assetsExcel: () => downloadFile('/api/reports/assets/excel', 'ativos.xlsx'),
  contractPDF: (id, number) => downloadFile(`/api/reports/contracts/${id}/pdf`, `contrato_${number}.pdf`),
  financialPDF: (month, year) => downloadFile(`/api/reports/financial/pdf?month=${month||''}&year=${year||''}`, 'relatorio_financeiro.pdf'),
  financialExcel: (month, year) => downloadFile(`/api/reports/financial/excel?month=${month||''}&year=${year||''}`, 'financeiro.xlsx'),
};
